import {COPILOT_AGENTS_PATH} from '@github-ui/agent-sessions/agents-plugin'
import {NewTaskChatInput} from '@github-ui/agent-sessions/components/new-task-chat-input/NewTaskChatInput'
import {
  type FailedAPITaskCreationAttempt,
  getAPITaskParamsFromAttempt,
  TaskCreationFailedBannerList,
} from '@github-ui/agent-sessions/components/TaskCreationFailedBanner'
import {useCreateAPITask} from '@github-ui/agent-sessions/hooks/use-create-api-task'
import {
  type StartLocalDeviceSessionInput,
  useCreateLocalDeviceSession,
} from '@github-ui/agent-sessions/hooks/use-create-local-device-session'
import {
  type FetchTasksResponse,
  getAgentTasksQueryKeyForMutation,
  getFlatTaskList,
  PAGE_SIZE,
  setFlatTaskList,
} from '@github-ui/agent-sessions/hooks/useTasks'
import type {AgentQuotaSnapshot} from '@github-ui/agent-sessions/types/agent-quota-snapshot'
import type {CreateAPITaskParams} from '@github-ui/agent-sessions/types/api-task'
import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import {ChatInput, type ChatInputRef} from '@github-ui/copilot-chat/components/ChatInput'
import {useEntitlement} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {FreeChatExceededBanner} from '@github-ui/copilot-chat/components/quota/FreeChatExceededBanner'
import {QuotaBanner} from '@github-ui/copilot-chat/components/quota/QuotaBanner'
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {useQuotaBanner} from '@github-ui/copilot-chat/hooks/use-quota-banner'
import {ImmersivePluginsProvider} from '@github-ui/copilot-chat/plugin/registry'
import {isPaidUser} from '@github-ui/copilot-chat/utils/copilot-chat-entitlement'
import type {CopilotChatManager} from '@github-ui/copilot-chat/utils/copilot-chat-manager'
import type {CopilotChatState} from '@github-ui/copilot-chat/utils/copilot-chat-reducer'
import {
  type AgentDropdownRepository,
  type CopilotChatCommandType,
  type CopilotChatModel,
  type CopilotChatOrg,
  type CopilotChatPayload,
  type CopilotChatReference,
  CopilotLicenseType,
  ModeSupportName,
} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {copilotLocalStorage} from '@github-ui/copilot-chat/utils/copilot-local-storage'
import {CopilotChatProvider} from '@github-ui/copilot-chat/utils/CopilotChatContext'
import {useGetChatState} from '@github-ui/copilot-chat/utils/ObservableChatContext'
import {CopilotChatInputHeader} from '@github-ui/copilot-chat-input-header'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {CommandStartersAndSuggestions} from '@github-ui/copilot-immersive-v1/components/NewConversation/CommandStartersAndSuggestions'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useImmersivePlugins} from '@github-ui/copilot-immersive-v1/hooks/use-immersive-plugins'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
/* eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports --
   preexisting violation https://github.com/github/react-platform/issues/1135 */
import {RelayEnvironmentProvider, relayEnvironmentWithMissingFieldHandlerForNode} from '@github-ui/relay-environment'
import {useNavigate} from '@github-ui/use-navigate'
import {CommentIcon, XIcon} from '@primer/octicons-react'
import {Banner, IconButton, Tooltip} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import styles from './CopilotChatInputPartial.module.css'

const environment = relayEnvironmentWithMissingFieldHandlerForNode()

export interface CopilotChatInputPartialProps extends CopilotChatPayload {
  searchWorkerFilePath: string
  ssoOrganizations?: CopilotChatOrg[]
  showHeader?: boolean
  userIsOnboarding?: boolean
  displayName?: string
  dashboardModulesEnabled?: boolean
  hasCEorCBAccess?: boolean
  graphqlApiUrl?: string
  /** for testing only */
  testReducerState?: CopilotChatState
}

export function CopilotChatInputPartial(props: CopilotChatInputPartialProps) {
  const [failedTasks, setFailedTasks] = useState<FailedAPITaskCreationAttempt[]>([])

  const taskListMutationConfig = useMemo(
    () => ({
      queryKey: getAgentTasksQueryKeyForMutation(),
      pageSize: PAGE_SIZE,
      getFlatTaskList,
      setFlatTaskList,
    }),
    [],
  )

  const {createTask} = useCreateAPITask<FetchTasksResponse>({
    ...taskListMutationConfig,
    onError: (error, variables) => {
      setFailedTasks(prev => [
        {
          ...variables,
          errorMessage: error.message,
          timestamp: new Date().toISOString(),
        },
        ...prev,
      ])
    },
  })

  const localDeviceMutation = useCreateLocalDeviceSession<FetchTasksResponse>({
    optimisticTaskListConfig: taskListMutationConfig,
  })
  const onLocalDeviceSubmit = localDeviceMutation.mutateAsync

  const navigate = useNavigate()
  const {agentsEnabled, dashboardModulesEnabled} = props

  const handleUserSubmit = useCallback(
    async (
      content: string,
      selectedRepo: AgentDropdownRepository | null,
      selectedBranch: string,
      selectedAgent: SelectedAgent,
      _selectedMode?: unknown,
      _modelId?: string,
      options?: {references?: string[]},
    ) => {
      setFailedTasks([])
      const trimmedContent = content.trim()
      if (trimmedContent === '' || !selectedBranch || selectedBranch.trim() === '') return
      if (selectedRepo) {
        const shouldCreateTaskFromList = dashboardModulesEnabled ?? agentsEnabled
        sendEvent('dotcom_chat.activate', {
          target: 'DASHBOARD_INPUT_SUBMIT',
          mode: 'dashboard',
          defaultToTask: copilotFeatureFlags.defaultToAgentInput,
          defaultToTaskControl: copilotFeatureFlags.defaultToAgentControl,
          createTaskFromDashboardList: shouldCreateTaskFromList,
          askModeDropdown: copilotFeatureFlags.askModeDropdown,
        })
        if (isFeatureEnabled('copilot_default_to_task_input') && !shouldCreateTaskFromList) {
          // Navigate to the agents view if the NewTaskChatInput is being used and the agents list is not present.
          const params = JSON.stringify({
            text: content,
            selectedRepo,
            selectedBranch,
            selectedAgent,
            ...(options?.references?.length ? {references: options.references} : {}),
          })
          copilotLocalStorage.setTaskEntrypointMessage(params)
          navigate(COPILOT_AGENTS_PATH, {preventTurbo: true})
          return
        }

        // This mutation will automatically invalidate and refetch the tasks after completion
        createTask({
          nwo: selectedRepo.nameWithOwner,
          problemStatement: trimmedContent,
          baseRef: selectedBranch,
          eventType: 'dashboard',
          customAgent: selectedAgent?.subAgentSlug,
          agentId: selectedAgent?.topLevelAgentId,
        })
      }
    },
    [createTask, navigate, agentsEnabled, dashboardModulesEnabled],
  )

  const handleUserSubmitRef = useRef(handleUserSubmit)

  useEffect(() => {
    handleUserSubmitRef.current = handleUserSubmit
  }, [handleUserSubmit])

  useEffect(() => {
    sendEvent('dotcom_chat.activate', {
      target: `CHAT_INPUT_PARTIAL_LOADED`,
      mode: 'dashboard',
      defaultToTask: copilotFeatureFlags.defaultToAgentInput,
      defaultToTaskControl: copilotFeatureFlags.defaultToAgentControl,
      askModeDropdown: copilotFeatureFlags.askModeDropdown,
    })
  }, [])

  const plugins = useImmersivePlugins({
    payload: {
      sparkEnabled: props.sparkEnabled,
      agentsEnabled: props.agentsEnabled,
      dashboardModulesEnabled: props.dashboardModulesEnabled,
    },
    handleUserSubmitRef,
  })

  return (
    <RelayEnvironmentProvider environment={environment}>
      <ImmersivePluginsProvider plugins={plugins}>
        <CopilotChatProvider
          copilotChatPayload={props}
          mode="immersive"
          refs={[]}
          ssoOrganizations={props.ssoOrganizations ?? []}
          testReducerState={props.testReducerState}
          threadId={null}
          workerPath={props.searchWorkerFilePath}
        >
          <StandaloneInput
            showHeader={props.showHeader}
            userIsOnboarding={props.userIsOnboarding}
            displayName={props.displayName}
            shouldClearTextOnSubmit={props.dashboardModulesEnabled}
            hasCEorCBAccess={props.hasCEorCBAccess}
            agentQuotaSnapshot={props.agentQuotaSnapshot}
            handleUserSubmit={handleUserSubmit}
            agentsEnabled={props.agentsEnabled}
            failedTasks={failedTasks}
            setFailedTasks={setFailedTasks}
            createTask={createTask}
            onLocalDeviceSubmit={onLocalDeviceSubmit}
          />
        </CopilotChatProvider>
      </ImmersivePluginsProvider>
    </RelayEnvironmentProvider>
  )
}

interface StandaloneInputProps {
  showHeader?: boolean
  userIsOnboarding?: boolean
  displayName?: string
  shouldClearTextOnSubmit?: boolean
  hasCEorCBAccess?: boolean
  agentQuotaSnapshot?: AgentQuotaSnapshot
  handleUserSubmit: (
    content: string,
    selectedRepo: AgentDropdownRepository | null,
    selectedBranch: string,
    selectedAgent: SelectedAgent,
  ) => Promise<void>
  agentsEnabled?: boolean
  failedTasks: FailedAPITaskCreationAttempt[]
  setFailedTasks: React.Dispatch<React.SetStateAction<FailedAPITaskCreationAttempt[]>>
  createTask: (params: CreateAPITaskParams) => void
  onLocalDeviceSubmit: (input: StartLocalDeviceSessionInput) => Promise<unknown>
}

const HIDDEN_UNTIL_RESET_KEY = 'copilot-dashboard-input-hidden-until-reset'

function StandaloneInput({
  showHeader = false,
  userIsOnboarding = false,
  displayName,
  shouldClearTextOnSubmit = true,
  hasCEorCBAccess,
  agentQuotaSnapshot,
  handleUserSubmit,
  agentsEnabled,
  failedTasks,
  setFailedTasks,
  createTask,
  onLocalDeviceSubmit,
}: StandaloneInputProps) {
  const chatInputRef = useRef<ChatInputRef>(null)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLFormElement>(null)
  const [chatCommand, setChatCommand] = useState<CopilotChatCommandType | null>(null)
  const [activeModeName, setActiveModeName] = useState<string>(() => {
    const savedModeName = copilotLocalStorage.getActiveMode()
    return savedModeName ?? ModeSupportName.Ask
  })
  const [sendError, setSendError] = useState(false)
  const getChatState = useGetChatState()
  const {chatQuotaExceeded, isLicensedLimited, licenseType, plan, tokenBasedBillingEnabled} = useEntitlement()
  const quotaBanner = useQuotaBanner()
  const useTaskInput = isFeatureEnabled('copilot_default_to_task_input') && agentsEnabled
  const manager = useChatManager()

  // Helper function to safely access localStorage
  const getHiddenUntilResetState = () => {
    try {
      return localStorage.getItem(HIDDEN_UNTIL_RESET_KEY) === 'true'
    } catch {
      return false
    }
  }

  const isInputDisabled = tokenBasedBillingEnabled
    ? quotaBanner.show && quotaBanner.isBlocking // The input is disabled when users have exceeded their quota (i.e. the banner is in blocking mode)
    : isLicensedLimited && chatQuotaExceeded // The input is disabled only when users have a limited license AND have exceeded their quota

  // Track the user's preference to hide the input
  const [wantsToHide, setWantsToHide] = useState(() => getHiddenUntilResetState())

  // Compute whether input should actually be hidden:
  // Only hide if user wants to hide AND input is currently disabled
  // This automatically unhides when quota is restored without needing state updates in effects
  const isHiddenUntilReset = isInputDisabled && wantsToHide

  // Clean up localStorage when quota is restored (external system sync only, no state updates)
  useEffect(() => {
    if (!isInputDisabled && wantsToHide) {
      try {
        localStorage.removeItem(HIDDEN_UNTIL_RESET_KEY)
      } catch {
        // Ignore localStorage errors
      }
    }
  }, [isInputDisabled, wantsToHide])

  const handleHideUntilReset = useCallback(() => {
    try {
      localStorage.setItem(HIDDEN_UNTIL_RESET_KEY, 'true')
    } catch {
      // Ignore localStorage errors in environments where it's unavailable
    }
    setWantsToHide(true)
    sendEvent('dotcom_chat.activate', {
      target: 'DASHBOARD_QUOTA_EXCEEDED_HIDE',
      mode: 'immersive',
    })
  }, [])

  const handleSubmit = useCallback(
    async (message: string) => {
      if (licenseType === CopilotLicenseType.Unlicensed) {
        copilotLocalStorage.setEntrypointMessage({message})
        window.location.replace(
          `${window.location.origin}/github-copilot/signup?return_to=${encodeURIComponent(window.location.pathname)}`,
        )
        return
      }

      const state = getChatState()
      sendEvent('dotcom_chat.activate', {
        target: 'DASHBOARD_ENTRYPOINT_SEND',
        mode: 'immersive',
        richInput: true,
        defaultToTask: isFeatureEnabled('copilot_default_to_task_input'),
        defaultToTaskControl: isFeatureEnabled('copilot_default_to_task_input_control_group'),
      })
      const success = await sendMessage(manager, message, state.currentReferences, state.model)
      setSendError(!success)
    },
    [getChatState, manager, licenseType],
  )

  const handleRetry = useCallback(
    (attemptIndex: number) => {
      const attempt = failedTasks[attemptIndex]
      if (attempt) {
        setFailedTasks(prev => prev.filter((_, index) => index !== attemptIndex))
        createTask(getAPITaskParamsFromAttempt(attempt))
      }
    },
    [failedTasks, setFailedTasks, createTask],
  )

  const handleDismiss = useCallback(
    (attemptIndex: number) => {
      setFailedTasks(prev => prev.filter((_, index) => index !== attemptIndex))
    },
    [setFailedTasks],
  )

  const iceBreakersExperimentEnabled = copilotFeatureFlags.copilotIcebreakersExperimentDashboard

  return (
    <div className={styles.inputContainer}>
      {showHeader && displayName && (
        <div data-testid="copilot-chat-input-header" className={styles.header}>
          <CopilotChatInputHeader
            displayName={displayName}
            showZeroUserHeader={userIsOnboarding}
            alternateZeroUserHeaderEnabled={isFeatureEnabled('zero_user_dashboard_alternate_layout')}
            feedbackDialogEnabled={isFeatureEnabled('dashboard_universe_2025_feedback_dialog')}
            feedbackSubject="dashboard_universe_2025_feedback"
            hideLogo={isHiddenUntilReset}
          />
        </div>
      )}
      {/* Hide input section and starters if user clicked X to hide until reset */}
      {!(isHiddenUntilReset && isInputDisabled) && (
        <>
          <div className={styles.inputSection}>
            {isInputDisabled && (
              <div className={styles.closeButtonContainer}>
                <Tooltip text="Hide until limits reset" direction="w">
                  <IconButton
                    icon={XIcon}
                    aria-label="Hide until limits reset"
                    size="small"
                    variant="invisible"
                    onClick={handleHideUntilReset}
                  />
                </Tooltip>
              </div>
            )}
            {sendError && (
              <Banner onDismiss={() => setSendError(false)} title="Error sending message" variant="critical">
                There was an error sending your message. Please try again.
              </Banner>
            )}
            {failedTasks.length > 0 && (
              <TaskCreationFailedBannerList attempts={failedTasks} onRetry={handleRetry} onDismiss={handleDismiss} />
            )}
            {useTaskInput ? (
              <NewTaskChatInput
                handleUserSubmit={handleUserSubmit}
                agentQuotaSnapshot={agentQuotaSnapshot}
                className={styles.input}
                shouldClearTextOnSubmit={shouldClearTextOnSubmit}
                onLocalDeviceSubmit={onLocalDeviceSubmit}
              />
            ) : (
              <ChatInput
                className={styles.input}
                disableAutoFocus
                disabled={isInputDisabled}
                activeModeName={activeModeName}
                onClickMode={setActiveModeName}
                modes={[
                  {
                    name: ModeSupportName.Ask,
                    supportsAttachments: true,
                    supportsModelSwitching: licenseType !== CopilotLicenseType.Unlicensed,
                    supportsSpaces: true,
                    supportsAutocompletion: true,
                    icon: CommentIcon,
                    placeholder: 'Ask anything or type @ to add context',
                  },
                ]}
                // Required to show the model picker, though we won't need to start new threads
                // since this is always a new conversation.
                onNewThreadSelected={async () => {}}
                onSubmit={handleSubmit}
                chatInputRef={chatInputRef}
                textAreaRef={textAreaRef}
                containerRef={containerRef}
                onChatCommandChange={setChatCommand}
                isNewConversation
                shouldClearTextOnCommandSubmit={!!(!hasCEorCBAccess || shouldClearTextOnSubmit)}
                slashCommandsEnabled
                source="dashboard"
              />
            )}
            {!tokenBasedBillingEnabled && isInputDisabled && <FreeChatExceededBanner />}
            {tokenBasedBillingEnabled && isInputDisabled && <QuotaBanner {...quotaBanner} />}
          </div>
          {!isInputDisabled &&
            !useTaskInput &&
            (copilotFeatureFlags.chatInputCommands ? (
              <CommandStartersAndSuggestions
                chatCommand={chatCommand}
                chatInputRef={chatInputRef}
                textAreaRef={textAreaRef}
                containerRef={containerRef}
                iceBreakersExperimentEnabled={iceBreakersExperimentEnabled}
                paidUser={isPaidUser(plan)}
                activeModeName={activeModeName}
              />
            ) : null)}
        </>
      )}
    </div>
  )
}

export async function sendMessage(
  manager: CopilotChatManager,
  message: string,
  references?: CopilotChatReference[],
  model?: CopilotChatModel,
): Promise<boolean> {
  const cssClass = 'appMain--fadeOut'
  document.querySelector('.application-main')?.classList.add(cssClass)

  // Extract space reference if it exists to pass as customCopilotId for routing and thread creation
  const spaceRef = references?.find(ref => ref.type === 'copilot-space')
  const customCopilotId = spaceRef ? {id: spaceRef.id, owner: spaceRef.owner} : null

  let success = false

  try {
    const result = await manager.sendChatMessage({
      thread: null,
      content: message,
      references: references ?? [],
      model,
      customCopilotId,
      // create the thread and upload images but do not send the message yet
      noSend: true,
    })
    if (!result) return false
    const {thread, references: filteredReferences, mediaContent} = result
    const targetUrl = `/copilot/c/${thread.id}`
    copilotLocalStorage.setEntrypointMessage({
      message,
      references: filteredReferences,
      model,
      mediaContent,
      threadID: thread.id,
    })

    success = true
    setTimeout(() => {
      window.location.href = targetUrl
    }, 200)
  } finally {
    if (!success) {
      document.querySelector('.application-main')?.classList.remove(cssClass)
    }
  }
  return success
}
